import { useEffect, useState } from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Gift, Copy, QrCode, Popcorn, Candy, Trophy, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchRadStats, fetchRadSpins } from "@/lib/api";

const PRIZE_META = {
  niks: { label: "Niks", icon: XCircle, color: "text-[#7A6C96]" },
  popcorn: { label: "Popcorn", icon: Popcorn, color: "text-gold" },
  suikerspin: { label: "Suikerspin", icon: Candy, color: "text-pink-400" },
  jackpot: { label: "Jackpot", icon: Trophy, color: "text-mystic-light" },
};

export default function AdminRad({ token }) {
  const [stats, setStats] = useState(null);
  const [spins, setSpins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [radUrl, setRadUrl] = useState("");

  useEffect(() => {
    setRadUrl(`${window.location.origin}/rad`);
    Promise.all([fetchRadStats(token), fetchRadSpins(token)])
      .then(([s, list]) => {
        setStats(s);
        setSpins(list);
      })
      .catch(() => toast.error("Kon rad-data niet laden"))
      .finally(() => setLoading(false));
  }, [token]);

  const copyUrl = () => {
    navigator.clipboard.writeText(radUrl);
    toast.success("Link gekopieerd!");
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&bgcolor=0B0716&color=E5B942&margin=15&data=${encodeURIComponent(radUrl)}`;

  return (
    <section className="mb-12" data-testid="admin-rad-section">
      <div className="flex items-center gap-3 mb-5">
        <Gift className="w-5 h-5 text-gold" />
        <h2 className="font-display text-2xl font-bold">Gelukswiel</h2>
        <span className="text-xs text-[#7A6C96] uppercase tracking-widest">
          {stats?.total || 0} keer gedraaid totaal
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {Object.entries(PRIZE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = stats?.by_prize?.[key] || 0;
          return (
            <div key={key} className="glass rounded-2xl p-5" data-testid={`rad-stat-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${meta.color}`} />
                <span className="text-[10px] uppercase tracking-widest text-[#7A6C96]">{meta.label}</span>
              </div>
              <div className="font-display text-3xl font-bold">{count}</div>
            </div>
          );
        })}
        <div className="glass rounded-2xl p-5 md:col-span-3 flex flex-col sm:flex-row gap-6 items-center">
          <div className="shrink-0 rounded-xl overflow-hidden bg-night border border-gold/30">
            <img src={qrSrc} alt="QR code voor gelukswiel" width="160" height="160" data-testid="rad-qr-image" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs uppercase tracking-widest text-gold mb-2 flex items-center justify-center sm:justify-start gap-2">
              <QrCode className="w-4 h-4" />
              QR-code & link voor het rad
            </div>
            <p className="text-sm text-[#B5A9C9] mb-3">
              Scan of deel deze link. Bezoekers kunnen 1× per dag draaien.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <code className="flex-1 glass rounded-lg px-3 py-2 text-xs text-gold break-all" data-testid="rad-url">{radUrl}</code>
              <Button
                onClick={copyUrl}
                variant="outline"
                className="border-gold/40 text-gold hover:bg-gold/10"
                data-testid="rad-copy-button"
              >
                <Copy className="w-4 h-4 mr-2" />
                Kopieer
              </Button>
              <a
                href={qrSrc}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-gold/40 text-gold text-sm hover:bg-gold/10 transition-colors"
                data-testid="rad-download-qr"
              >
                QR downloaden
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gold/10 text-xs uppercase tracking-widest text-[#B5A9C9]">
          Laatste 20 spins
        </div>
        {loading ? (
          <div className="p-6 text-center text-[#7A6C96]">Laden...</div>
        ) : spins.length === 0 ? (
          <div className="p-6 text-center text-[#7A6C96]">Nog niemand heeft gedraaid.</div>
        ) : (
          <ul className="divide-y divide-gold/5" data-testid="rad-spins-list">
            {spins.slice(0, 20).map((s) => {
              const meta = PRIZE_META[s.prize] || PRIZE_META.niks;
              const Icon = meta.icon;
              return (
                <li key={s.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`rad-spin-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`w-4 h-4 ${meta.color} shrink-0`} />
                    <span className="font-semibold">{meta.label}</span>
                    <span className="text-[#7A6C96] text-xs capitalize">· {s.device}</span>
                  </div>
                  <span className="text-xs text-[#7A6C96] shrink-0">
                    {format(new Date(s.created_at), "d MMM yyyy · HH:mm", { locale: nl })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
